import React, { useState, useEffect, useRef } from 'react';
import styles from './SearchableSelect.module.css';

const SearchableSelect = ({
    label,
    placeholder,
    value,
    onChange,
    onSearch,
    options = [],
    loading = false,
    displayKey = 'name',
    valueKey = '_id'
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedValue, setSelectedValue] = useState('');
    const dropdownRef = useRef(null);
    const inputRef = useRef(null);

    useEffect(() => {
        // Set search term when value prop changes and we have options
        if (value && options.length > 0) {
            const selectedOption = options.find(option => option[valueKey] === value);
            if (selectedOption) {
                setSearchTerm(selectedOption[displayKey]);
                setSelectedValue(value);
            }
        } else if (!value && selectedValue) {
            // Only clear if we had a selected value and now it's being cleared
            setSearchTerm('');
            setSelectedValue('');
        }
    }, [value, options, displayKey, valueKey, selectedValue]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
                // Don't reset search term when clicking outside - keep what user has typed
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleInputChange = (e) => {
        const newValue = e.target.value;
        setSearchTerm(newValue);
        setIsOpen(true);

        // Call search function with debouncing
        if (onSearch) {
            onSearch(newValue);
        }
    };

    const handleInputFocus = () => {
        setIsOpen(true);
        // Keep whatever text the user has typed - don't clear it
    };

    const handleOptionSelect = (option) => {
        const selectedVal = option[valueKey];
        const selectedDisplay = option[displayKey];

        // Only update text when user selects an item
        setSearchTerm(selectedDisplay);
        setSelectedValue(selectedVal);
        setIsOpen(false);

        if (onChange) {
            onChange(selectedVal, option);
        }
    };

    const handleClear = () => {
        setSearchTerm('');
        setSelectedValue('');
        setIsOpen(false);
        if (onChange) {
            onChange('', null);
        }
    };

    return (
        <div className={styles.container} ref={dropdownRef}>
            <label className={styles.label}>{label}</label>
            <div className={styles.inputContainer}>
                <input
                    ref={inputRef}
                    type="text"
                    className={styles.input}
                    placeholder={placeholder}
                    value={searchTerm}
                    onChange={handleInputChange}
                    onFocus={handleInputFocus}
                />
                {selectedValue && searchTerm && (
                    <button
                        type="button"
                        className={styles.clearButton}
                        onClick={handleClear}
                    >
                        ×
                    </button>
                )}
                <div className={styles.dropdownIcon}>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6,9 12,15 18,9"></polyline>
                    </svg>
                </div>
            </div>

            {isOpen && (
                <div className={styles.dropdown}>
                    {loading ? (
                        <div className={styles.loadingItem}>
                            <span>Searching...</span>
                        </div>
                    ) : options.length > 0 ? (
                        options.map((option) => (
                            <div
                                key={option[valueKey]}
                                className={styles.option}
                                onClick={() => handleOptionSelect(option)}
                            >
                                {option[displayKey]}
                            </div>
                        ))
                    ) : searchTerm && searchTerm.trim().length >= 2 ? (
                        <div className={styles.noResults}>
                            No results found for "{searchTerm}"
                        </div>
                    ) : searchTerm && searchTerm.trim().length < 2 ? (
                        <div className={styles.noResults}>
                            Type at least 2 characters to search...
                        </div>
                    ) : (
                        <div className={styles.noResults}>
                            Start typing to search...
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default SearchableSelect;
